import React from 'react'
import {Text, View} from '@react-pdf/renderer'
import {baseStyles, colors} from './styles'

// ─────────────────────────────────────────────────────────────────────────
// Shared visual primitives for the disclosure PDFs. Components feed data
// into <Panel> + <DataRow> rather than re-deriving the panel/row chrome
// every time, so a row-style tweak only touches styles.js + this file.
// API docs for the underlying @react-pdf/renderer primitives:
//   https://react-pdf.org/components
// ─────────────────────────────────────────────────────────────────────────

// Panel: rounded, hairline-bordered box with a bolded title at the top.
// Optional right-side `accessory` (e.g. a chevron) renders inside the header.
export const Panel = ({title, accessory, children}) => (
    <View style={baseStyles.panel}>
        <View style={baseStyles.panelHeader}>
            <Text style={baseStyles.panelTitle}>{title}</Text>
            {accessory}
        </View>
        <View style={baseStyles.panelBody}>
            {children}
        </View>
    </View>
)

// Variants for the right-hand value cell:
//   undefined / 'normal' — plain black
//   'bold'               — bold black
//   'blue'               — bold blue (used for the headline totals)
//   'green'              — green (used for positive amounts / rebates)
//   'muted'              — plain black at normal weight (semantic alias)
const valueVariantStyle = (variant) => {
    switch (variant) {
        case 'bold':  return baseStyles.rowValueBold
        case 'blue':  return baseStyles.rowValueBlue
        case 'green': return baseStyles.rowValueGreen
        case 'muted': return baseStyles.rowValueMuted
        default:      return null
    }
}

// One label/value line inside a panel.
//   isTotal — adds the thicker top border + bolds both label and value
//   isLast  — drops the hairline bottom border (visual cleanup for the
//             final row of a panel; harmless if applied incorrectly)
export const DataRow = ({label, value, variant, isTotal = false, isLast = false}) => {
    const variantStyle = valueVariantStyle(variant)
    return (
        <View
            style={[
                baseStyles.dataRow,
                isTotal && baseStyles.totalRow,
                isLast  && baseStyles.dataRowLast,
            ].filter(Boolean)}
        >
            <Text
                style={[
                    baseStyles.rowLabel,
                    isTotal && baseStyles.totalRowLabel,
                ].filter(Boolean)}
            >
                {label}
            </Text>
            <Text
                style={[
                    baseStyles.rowValue,
                    variantStyle,
                    isTotal && baseStyles.totalRowValue,
                ].filter(Boolean)}
            >
                {value}
            </Text>
        </View>
    )
}

// Vehicle-info pill: small uppercase label above a regular-weight value.
// Used in the row below the topbar (Lender / Type / MSRP / State / County).
// `style` lets the caller override the block (e.g. cap the Lender pill's
// maxWidth so a long lender name wraps onto two lines instead of pushing
// the rest of the row off-screen).
export const Pill = ({label, value, style}) => (
    <View style={[baseStyles.pillBlock, style].filter(Boolean)}>
        <Text style={baseStyles.pillLabel}>{label}</Text>
        <Text style={baseStyles.pillValue}>{value}</Text>
    </View>
)

export const PillDivider = () => (
    <View style={baseStyles.pillDivider}/>
)

// Re-export the design tokens so downstream files can read e.g. `colors.blue`
// without importing styles separately.
export {colors}
